import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Bell, Check, CheckCheck, Trash2, X } from 'lucide-react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { csrfHeaders } from '../../utils/csrfToken'
import { useWebSocketContext } from '@contexts/WebSocketContext'

const API = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'

const TYPE_COLORS = {
  trade: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  signal:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  alert: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  system:
    'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  info: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
}

export default function NotificationCenter({ onUnreadCountChange }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const panelRef = useRef(null)

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, {
        withCredentials: true
      })
      if (data.success) {
        setUnreadCount(data.data.count)
        onUnreadCountChange?.(data.data.count)
      }
    } catch {
      /* silent */
    }
  }, [onUnreadCountChange])

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(`${API}/notifications?limit=20`, {
        withCredentials: true
      })
      if (data.success) setNotifications(data.documents)
    } catch {
      /* silent */
    }
    setLoading(false)
  }, [])

  const ws = useWebSocketContext()

  // Initial REST fetch for unread count
  useEffect(() => {
    fetchUnreadCount()
  }, [fetchUnreadCount])

  // Live updates via WebSocket
  useEffect(() => {
    if (!ws.isAuthenticated) return
    ws.subscribe(['notifications'])
    const unsub = ws.on('notification_created', () => {
      setUnreadCount(c => {
        const next = c + 1
        onUnreadCountChange?.(next)
        return next
      })
    })
    return () => {
      ws.unsubscribe(['notifications'])
      unsub()
    }
  }, [onUnreadCountChange, ws])

  // Fetch full list when opened
  useEffect(() => {
    if (open) fetchNotifications()
  }, [open, fetchNotifications])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = e => {
      if (panelRef.current && !panelRef.current.contains(e.target))
        setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const markAsRead = async id => {
    try {
      await axios.patch(
        `${API}/notifications/${id}/read`,
        {},
        { withCredentials: true, headers: csrfHeaders() }
      )
      setNotifications(prev =>
        prev.map(n => (n._id === id ? { ...n, read: true } : n))
      )
      setUnreadCount(c => Math.max(0, c - 1))
    } catch {
      /* silent */
    }
  }

  const markAllRead = async () => {
    try {
      await axios.patch(
        `${API}/notifications/read-all`,
        {},
        { withCredentials: true, headers: csrfHeaders() }
      )
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch {
      /* silent */
    }
  }

  const deleteNotification = async id => {
    try {
      await axios.delete(`${API}/notifications/${id}`, {
        withCredentials: true,
        headers: csrfHeaders()
      })
      const removed = notifications.find(n => n._id === id)
      setNotifications(prev => prev.filter(n => n._id !== id))
      if (removed && !removed.read) setUnreadCount(c => Math.max(0, c - 1))
    } catch {
      /* silent */
    }
  }

  const openNotification = async notification => {
    if (!notification.read) {
      try {
        await markAsRead(notification._id)
      } catch {
        /* navigation should not depend on read-state persistence */
      }
    }

    const route = notification.metadata?.route
    if (route) {
      setOpen(false)
      navigate(route)
    }
  }

  const timeAgo = date => {
    const seconds = Math.floor((Date.now() - new Date(date)) / 1000)
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
          role="region"
          aria-label="Notification center"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  aria-label="Mark all as read"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Close notifications"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No notifications yet
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n._id}
                  onClick={() => openNotification(n)}
                  className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                    n.metadata?.route ? 'cursor-pointer' : ''
                  } ${
                    !n.read ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TYPE_COLORS[n.type] || TYPE_COLORS.info}`}
                        >
                          {n.type}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {timeAgo(n.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {n.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                        {n.message}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!n.read && (
                        <button
                          onClick={event => {
                            event.stopPropagation()
                            markAsRead(n._id)
                          }}
                          className="p-1 text-gray-400 hover:text-primary-500"
                          aria-label="Mark as read"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={event => {
                          event.stopPropagation()
                          deleteNotification(n._id)
                        }}
                        className="p-1 text-gray-400 hover:text-red-500"
                        aria-label="Delete notification"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
